import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Checkbox, MessageBar, MessageBarBody, Spinner, Text } from "@fluentui/react-components";
import { ArrowDownload20Regular, ArrowSync20Regular, Send20Regular, Sparkle20Regular } from "@fluentui/react-icons";
import { generateMinutes, getMinutes, listMinutes, sendMinutes, updateActionItem } from "../spurt/meetings";
import type { ActionItem, Minutes, MinutesOut } from "../spurt/meetings";
import { minutesErrorText } from "./meetingErrors";
import type { MinutesAction } from "./meetingErrors";
import { fmtDuration, safeFileName, saveText } from "./meetingFormat";
import css from "./Meetings.module.css";

const STATUS_WORD: Record<ActionItem["status"], string> = { open: "Open", in_progress: "In progress", done: "Done", canceled: "Cancelled" };

/** Plain-text version of the minutes, built in the browser (no export endpoint needed). */
export function minutesToText(m: Minutes): string {
  const out: string[] = [];
  const section = (head: string, lines: string[]) => {
    if (lines.length === 0) return;
    out.push("", head.toUpperCase(), ...lines);
  };
  out.push(`Minutes: ${m.title}`);
  if (m.summary) section("Summary", [m.summary]);
  if (m.topics.length) {
    out.push("", "TOPICS");
    m.topics.forEach((t, i) => {
      out.push(`${i + 1}. ${t.title}`);
      t.points.forEach((p) => out.push(`   - ${p}`));
    });
  }
  section("Decisions", m.decisions.map((d) => `- ${d}`));
  section(
    "Action items",
    m.actionItems.map((a) => {
      const bits = [a.ownerName ? `owner: ${a.ownerName}` : "", a.dueMs ? `due: ${new Date(a.dueMs).toLocaleDateString()}` : "", `priority: ${a.priority}`].filter(Boolean);
      return `- [${a.status === "done" ? "x" : " "}] ${a.description} (${bits.join(", ")}; ${STATUS_WORD[a.status].toLowerCase()})`;
    }),
  );
  section("Open questions", m.openQuestions.map((q) => `- ${q}`));
  section("Next steps", m.nextSteps.map((q) => `- ${q}`));
  section("Participants", m.participants.map((p) => `- ${p.name} (${fmtDuration(p.totalMs)})`));
  section("Limitations", (m.limitations ?? []).map((l) => `- ${l}`));
  return out.join("\n") + "\n";
}

export interface MinutesPanelProps {
  meetingId: string;
  /** when known, hides the host buttons from people who can't use them; the server still decides */
  isHost?: boolean | undefined;
}

export const MinutesPanel: React.FC<MinutesPanelProps> = ({ meetingId, isHost }) => {
  const [minutes, setMinutes] = useState<MinutesOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState<"" | "generate" | "regenerate" | "send">("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const list = await listMinutes({ meetingId, limit: 1 });
        const first = list[0];
        const full = first ? await getMinutes(first.id) : null;
        if (!alive.current) return;
        setMinutes(full);
        setLoadError("");
      } catch (e) {
        if (alive.current) setLoadError(minutesErrorText(e, "load"));
      } finally {
        if (alive.current) setLoading(false);
      }
    },
    [meetingId],
  );

  useEffect(() => {
    setMinutes(null);
    setMsg(null);
    void load();
  }, [load]);

  // while the server is still writing them, look again every few seconds
  const generating = minutes?.genState === "generating";
  useEffect(() => {
    if (!generating) return;
    const id = window.setInterval(() => void load(true), 5000);
    return () => window.clearInterval(id);
  }, [generating, load]);

  const canManage = minutes ? minutes.can.manage : isHost !== false;

  const generate = async (regenerate: boolean) => {
    const kind: MinutesAction = regenerate ? "regenerate" : "generate";
    setBusy(kind);
    setMsg(null);
    try {
      const r = await generateMinutes(meetingId, regenerate);
      if (!alive.current) return;
      setMinutes(r.minutes);
      setMsg({ ok: true, text: r.generated ? "Minutes generated." : "These minutes already existed, so nothing new was written." });
    } catch (e) {
      if (alive.current) setMsg({ ok: false, text: minutesErrorText(e, kind) });
    } finally {
      if (alive.current) setBusy("");
    }
  };

  const send = async () => {
    if (!minutes) return;
    setBusy("send");
    setMsg(null);
    try {
      await sendMinutes(minutes.id, { includeInvitees: true });
      if (!alive.current) return;
      setMsg({ ok: true, text: "The minutes were sent to the attendees." });
      void load(true);
    } catch (e) {
      if (alive.current) setMsg({ ok: false, text: minutesErrorText(e, "send") });
    } finally {
      if (alive.current) setBusy("");
    }
  };

  const tick = async (item: ActionItem, done: boolean) => {
    if (!minutes) return;
    const next: ActionItem["status"] = done ? "done" : "open";
    const swap = (status: ActionItem["status"]) => setMinutes((m) => (m ? { ...m, actionItems: m.actionItems.map((a) => (a.id === item.id ? { ...a, status } : a)) } : m));
    swap(next);
    try {
      await updateActionItem(minutes.id, item.id, { status: next });
    } catch (e) {
      if (!alive.current) return;
      swap(item.status);
      setMsg({ ok: false, text: minutesErrorText(e, "tick") });
    }
  };

  const download = () => {
    if (minutes) saveText(`${safeFileName(minutes.title)}-minutes.txt`, minutesToText(minutes));
  };

  if (loading) return <Spinner label="Loading minutes…" className={css.pad} />;
  if (loadError)
    return (
      <div className={css.stack}>
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{loadError}</MessageBarBody>
        </MessageBar>
        <Button onClick={() => void load()}>Try again</Button>
      </div>
    );

  const banner = msg && (
    <MessageBar intent={msg.ok ? "success" : "error"} role={msg.ok ? "status" : "alert"}>
      <MessageBarBody>{msg.text}</MessageBarBody>
    </MessageBar>
  );

  if (!minutes) {
    return (
      <div className={css.stack} aria-label="Minutes">
        <Text className={css.muted}>No minutes have been written for this meeting yet.</Text>
        {banner}
        {canManage && (
          <div className={css.row}>
            <Button appearance="primary" icon={busy === "generate" ? <Spinner size="tiny" /> : <Sparkle20Regular />} disabled={busy !== ""} onClick={() => void generate(false)}>
              Generate minutes
            </Button>
            <Button icon={<ArrowSync20Regular />} onClick={() => void load()}>
              Refresh
            </Button>
          </div>
        )}
      </div>
    );
  }

  const m = minutes;
  return (
    <div className={css.stack} aria-label="Minutes">
      <div className={css.row}>
        <Badge appearance="tint" color={m.status === "sent" ? "success" : "informative"}>
          {m.status === "sent" ? "Sent" : "Draft"}
        </Badge>
        {m.genState === "generating" && <Badge appearance="tint" color="warning">Being written…</Badge>}
        {m.genState === "failed" && <Badge appearance="tint" color="danger">Failed</Badge>}
      </div>

      {m.genState === "failed" && (
        <MessageBar intent="error">
          <MessageBarBody>{m.genError || "The minutes could not be written."}</MessageBarBody>
        </MessageBar>
      )}
      {banner}

      {m.summary && (
        <section>
          <h3 className={css.h3}>Summary</h3>
          <Text>{m.summary}</Text>
        </section>
      )}

      {m.topics.length > 0 && (
        <section>
          <h3 className={css.h3}>Topics</h3>
          {m.topics.map((t, i) => (
            <div key={i} className={css.topic}>
              <strong>{t.title}</strong>
              <ul className={css.list}>
                {t.points.map((p, j) => (
                  <li key={j}>{p}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {m.decisions.length > 0 && (
        <section>
          <h3 className={css.h3}>Decisions</h3>
          <ul className={css.list}>
            {m.decisions.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </section>
      )}

      {m.actionItems.length > 0 && (
        <section>
          <h3 className={css.h3}>Action items</h3>
          <ul className={css.plain}>
            {m.actionItems.map((a) => (
              <li key={a.id} className={css.action}>
                <Checkbox
                  checked={a.status === "done"}
                  disabled={!m.can.manage}
                  aria-label={`Mark done: ${a.description}`}
                  onChange={(_, d) => void tick(a, d.checked === true)}
                  label={<span className={a.status === "done" ? css.struck : undefined}>{a.description}</span>}
                />
                <span className={css.muted}>
                  {[a.ownerName, a.dueMs ? `due ${new Date(a.dueMs).toLocaleDateString()}` : "", a.priority !== "medium" ? `${a.priority} priority` : "", a.status === "in_progress" || a.status === "canceled" ? STATUS_WORD[a.status].toLowerCase() : ""].filter(Boolean).join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {m.openQuestions.length > 0 && (
        <section>
          <h3 className={css.h3}>Open questions</h3>
          <ul className={css.list}>
            {m.openQuestions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </section>
      )}

      {m.nextSteps.length > 0 && (
        <section>
          <h3 className={css.h3}>Next steps</h3>
          <ul className={css.list}>
            {m.nextSteps.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </section>
      )}

      {m.participants.length > 0 && (
        <section>
          <h3 className={css.h3}>Participants</h3>
          <ul className={css.list}>
            {m.participants.map((p) => (
              <li key={p.userId || p.name}>
                {p.name} <span className={css.muted}>({fmtDuration(p.totalMs)})</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(m.limitations ?? []).length > 0 && (
        <section>
          <h3 className={css.h3}>Limitations</h3>
          <ul className={css.list}>
            {(m.limitations ?? []).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </section>
      )}

      <div className={css.row}>
        <Button icon={<ArrowDownload20Regular />} onClick={download}>
          Download
        </Button>
        {m.can.manage && (
          <>
            <Button icon={busy === "regenerate" ? <Spinner size="tiny" /> : <ArrowSync20Regular />} disabled={busy !== ""} onClick={() => void generate(true)}>
              Regenerate
            </Button>
            <Button appearance="primary" icon={busy === "send" ? <Spinner size="tiny" /> : <Send20Regular />} disabled={busy !== "" || m.genState !== "ready"} onClick={() => void send()}>
              Send to attendees
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default MinutesPanel;
